import { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { googleSearchUrl, appleSearchUrl, googleDirUrl, appleDirUrl, mapsHref } from "@/lib/maps";

/**
 * Clickable location that lets the user pick Google Maps or Apple Maps.
 * Props:
 *  - parts: array of address pieces (street, city, state, zip)
 *  - mode: "search" (find place) | "directions" (turn-by-turn from current location)
 *  - direct: when true, a single tap opens the device's default map app (no picker)
 *  - testid, className (on the trigger), wrapperClassName, align ("left"|"right")
 */
export default function MapLink({
    parts,
    children,
    mode = "search",
    direct = false,
    testid = "map-link",
    className = "",
    wrapperClassName = "relative inline-flex",
    align = "left",
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // One-tap direct link: hand the address straight to the OS default map app.
    if (direct) {
        const href = mapsHref(parts);
        if (!href) return <span className={className}>{children}</span>;
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className={className}>
                {children}
            </a>
        );
    }

    const g = mode === "directions" ? googleDirUrl(parts) : googleSearchUrl(parts);
    const a = mode === "directions" ? appleDirUrl(parts) : appleSearchUrl(parts);
    if (!g) return <span className={className}>{children}</span>;

    return (
        <span className={wrapperClassName} ref={ref}>
            <button
                type="button"
                data-testid={testid}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
                className={className}
            >
                {children}
            </button>
            {open && (
                <div
                    data-testid={`${testid}-menu`}
                    className={`absolute z-50 top-full mt-2 w-48 bg-[#141414] border border-[#2a2a2a] shadow-2xl ${align === "right" ? "right-0" : "left-0"}`}
                >
                    <div className="px-3 py-2 border-b border-[#2a2a2a] text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                        {mode === "directions" ? "Get directions" : "Open in maps"}
                    </div>
                    <a href={g} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-google`} onClick={() => setOpen(false)} className="flex items-center justify-between px-3 py-2 text-sm text-white hover:bg-[#0f0f0f] hover:text-[#22c55e] transition-colors">
                        Google Maps <ExternalLink className="h-3 w-3" />
                    </a>
                    <a href={a} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-apple`} onClick={() => setOpen(false)} className="flex items-center justify-between px-3 py-2 text-sm text-white hover:bg-[#0f0f0f] hover:text-[#22c55e] transition-colors border-t border-[#2a2a2a]">
                        Apple Maps <ExternalLink className="h-3 w-3" />
                    </a>
                </div>
            )}
        </span>
    );
}
